'use client';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { TableIcon, X, ChevronRight, Plus } from 'lucide-react';
import { BaseTooltip } from '@/components';
import { MarkerPopover } from './MarkerPopover';
import { HarmonicsPopover } from './HarmonicsPopover';
import { SideBandsPopover } from './SideBandsPopover';
import type { HarmonicSelection } from '../../types';

interface FloatingChartToolbarProps {
  // Marker state
  onEnterMarkerSelectionMode?: () => void;
  isMarkerSelectionMode?: boolean;
  markerLabel?: string;

  // Custom vertical lines
  customVerticalLines?: any;
  onEnterCustomLineMode?: (groupId: string) => void;
  isCustomLineMode?: string | null; // groupId of active custom line mode

  // Harmonics state
  harmonicSelections?: HarmonicSelection[];
  onAddHarmonicSelection?: (selection: HarmonicSelection) => void;
  onUpdateHarmonicSelection?: (id: string, updates: Partial<HarmonicSelection>) => void;
  onRemoveHarmonicSelection?: (id: string) => void;
  onRemoveAllHarmonics?: () => void;
  onFocusHarmonic?: (id: string) => void;
  onResetHarmonic?: (id: string) => void;
  onEnterHarmonicSelectionMode?: () => void;
  onGenerateHarmonics?: (
    startingPoint: number,
    multiplier: number,
    label: string,
    intervalType?: 'seconds' | 'minutes' | 'hours' | 'days',
    xAxisType?: 'date' | 'numeric' | 'category',
    chartId?: string
  ) => void;
  isHarmonicSelectionMode?: boolean;

  // Side Bands state
  sideBandsSelections?: HarmonicSelection[];
  onEnterSideBandsSelectionMode?: () => void;
  isSideBandsSelectionMode?: boolean;

  // Values (comparison table)
  verticalLines?: any[];
  showComparisonTable?: boolean;
  onToggleCompareModal?: (open: boolean) => void;

  // Clear
  onClearLines?: () => void;

  // General state
  xAxisType?: 'date' | 'numeric' | 'category';
  isFeatureLoading?: boolean;
  disabled?: boolean;

  // Pending harmonic/sideband creation state
  pendingHarmonicCreation?: {
    centerPoint: number;
    chartId: string;
    sectionKey: string;
    type: 'harmonic' | 'sideBands';
    labelPrefix: string;
    color: string;
    xAxisType: 'date' | 'numeric' | 'category';
    groupId: string;
  } | null;

  // Marking tools visibility
  markingTools?: {
    markers?: boolean;
    harmonics?: boolean;
    sideBands?: boolean;
    measure?: boolean;
  };
}

export const FloatingChartToolbar: React.FC<FloatingChartToolbarProps> = ({
  onEnterMarkerSelectionMode,
  isMarkerSelectionMode = false,
  markerLabel = 'Markers',

  customVerticalLines,
  onEnterCustomLineMode,
  isCustomLineMode = null,

  harmonicSelections = [],
  onAddHarmonicSelection,
  onUpdateHarmonicSelection,
  onRemoveHarmonicSelection,
  onRemoveAllHarmonics,
  onFocusHarmonic,
  onResetHarmonic,
  onEnterHarmonicSelectionMode,
  onGenerateHarmonics,
  isHarmonicSelectionMode = false,

  sideBandsSelections = [],
  onEnterSideBandsSelectionMode,
  isSideBandsSelectionMode = false,

  verticalLines = [],
  showComparisonTable = false,
  onToggleCompareModal,

  onClearLines,

  xAxisType = 'numeric',
  isFeatureLoading = false,
  disabled = false,

  pendingHarmonicCreation = null,

  markingTools = {
    markers: true,
    harmonics: true,
    sideBands: true,
    measure: true,
  },
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Check if buttons should be disabled due to pending harmonic/sideband creation
  const isPendingCreation = !!pendingHarmonicCreation;

  return (
    <div
      className="absolute top-2 left-2 z-50 flex items-center gap-1"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setIsExpanded(false);
      }}
    >
        {/* Collapse/Expand Icon */}
        <div
          className={`transition-opacity duration-200 ${
            isHovering || isExpanded ? 'opacity-100' : 'opacity-0'
          }`}
          onMouseEnter={() => setIsExpanded(true)}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 bg-white/90 hover:bg-white shadow-md backdrop-blur-sm"
          >
            <ChevronRight
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          </Button>
        </div>

        {/* Toolbar Items */}
        <div
          className={`flex items-center gap-1 transition-all duration-200 bg-white/90 backdrop-blur-sm shadow-md rounded-md ${
            isExpanded ? 'opacity-100 translate-x-0 px-2 py-1' : 'opacity-0 -translate-x-2 pointer-events-none'
          }`}
        >
          {/* Marker Tool */}
          {markingTools?.markers && onEnterMarkerSelectionMode && (
            <MarkerPopover
              onEnterSelectionMode={onEnterMarkerSelectionMode}
              isSelectionMode={isMarkerSelectionMode}
              disabled={isFeatureLoading || isPendingCreation || disabled}
              label={markerLabel}
            />
          )}

          {/* Custom Vertical Line Buttons removed - they're in DataLayerToolbar now */}

        {/* Harmonics Tool */}
        {markingTools?.harmonics && onAddHarmonicSelection && (
          <HarmonicsPopover
            selections={harmonicSelections}
            onAddSelection={onAddHarmonicSelection}
            onUpdateSelection={onUpdateHarmonicSelection!}
            onRemoveSelection={onRemoveHarmonicSelection!}
            onRemoveAll={onRemoveAllHarmonics!}
            onFocus={onFocusHarmonic!}
            onReset={onResetHarmonic!}
            onEnterSelectionMode={onEnterHarmonicSelectionMode!}
            onGenerateHarmonics={onGenerateHarmonics!}
            isSelectionMode={isHarmonicSelectionMode}
            disabled={isFeatureLoading || isPendingCreation || disabled}
            xAxisType={xAxisType}
          />
        )}

        {/* Side Bands Tool */}
        {markingTools?.sideBands && onEnterSideBandsSelectionMode && (
          <SideBandsPopover
            selections={sideBandsSelections}
            onEnterSelectionMode={onEnterSideBandsSelectionMode}
            isSelectionMode={isSideBandsSelectionMode}
            disabled={isFeatureLoading || isPendingCreation || disabled}
            xAxisType={xAxisType}
          />
        )}

        {/* Values Toggle */}
        {markingTools?.measure && onToggleCompareModal && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-[#55687d] h-8"
            disabled={verticalLines?.length === 0 || isFeatureLoading || isHarmonicSelectionMode || isPendingCreation || disabled}
            onClick={() => onToggleCompareModal(!showComparisonTable)}
          >
            <TableIcon className="w-4 h-4" />
            {showComparisonTable ? 'Hide Values' : 'Values'}
          </Button>
        )}

        {/* Clear Button */}
        {verticalLines?.length > 0 && onClearLines && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-[#55687d] h-8"
            disabled={isFeatureLoading || isHarmonicSelectionMode || isPendingCreation || disabled}
            onClick={() => {
              onClearLines();
            }}
          >
            <X className="w-4 h-4" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};
